import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PPFQuoteFormProps {
  variant?: "hero" | "bottom";
}

const PPFQuoteForm = ({ variant = "hero" }: PPFQuoteFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    vehicle: "",
  });
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.fullName.trim() || !formData.phone.trim() || !formData.vehicle.trim()) {
      toast({
        title: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Insert into dedicated PPF quote submissions table
      const { error } = await supabase.from("ppf_quote_submissions").insert({
        full_name: formData.fullName.trim(),
        phone: formData.phone.trim(),
        vehicle: formData.vehicle.trim(),
        status: "new",
      });

      if (error) throw error;

      // Split name for email notification
      const nameParts = formData.fullName.trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "-";

      // Trigger email notification
      await supabase.functions.invoke("send-contact-notification", {
        body: {
          firstName: firstName,
          lastName: lastName,
          email: "ppf-landing@noemail.com",
          phone: formData.phone.trim(),
          vehicle: formData.vehicle.trim(),
          service: "Paint Protection Film",
          message: "PPF Quote Request from Google Ads Landing Page",
        },
      });

      // Fire Google Ads conversion event
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'conversion', {
          'send_to': 'AW-17910601966',
          'value': 1.0,
          'currency': 'USD'
        });
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "Something went wrong",
        description: "Please call us directly or try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-[#1A1A1A] border border-accent/30 rounded-lg p-6 text-center">
        <CheckCircle className="w-12 h-12 text-accent mx-auto mb-4" />
        <h3 className="text-xl font-heading font-bold text-foreground mb-2">
          Quote Request Received!
        </h3>
        <p className="text-muted-foreground text-sm">
          A PPF specialist will contact you within 2 hours during business hours to discuss your vehicle's protection options.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor={`fullName-${variant}`} className="text-foreground text-sm font-medium">
            Full Name
          </Label>
          <Input
            id={`fullName-${variant}`}
            type="text"
            placeholder="John Smith"
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            className="mt-1 bg-background border-white/20 text-foreground placeholder:text-muted-foreground focus:border-accent"
            required
          />
        </div>
        
        <div>
          <Label htmlFor={`phone-${variant}`} className="text-foreground text-sm font-medium">
            Phone Number
          </Label>
          <Input
            id={`phone-${variant}`}
            type="tel"
            placeholder="(702) 555-0123"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="mt-1 bg-background border-white/20 text-foreground placeholder:text-muted-foreground focus:border-accent"
            required
          />
        </div>
        
        <div>
          <Label htmlFor={`vehicle-${variant}`} className="text-foreground text-sm font-medium">
            Vehicle Make & Model
          </Label>
          <Input
            id={`vehicle-${variant}`}
            type="text"
            placeholder="2024 Porsche 911"
            value={formData.vehicle}
            onChange={(e) => setFormData({ ...formData, vehicle: e.target.value })}
            className="mt-1 bg-background border-white/20 text-foreground placeholder:text-muted-foreground focus:border-accent"
            required
          />
        </div>
        
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold py-6 text-base uppercase tracking-wide"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Submitting...
            </>
          ) : (
            "Get My PPF Quote"
          )}
        </Button>
        
        <p className="text-xs text-muted-foreground text-center">
          We'll call you within 2 hours to schedule your free consultation.
        </p>
      </div>
    </form>
  );
};

export default PPFQuoteForm;
